import React from 'react';
import type { FC, ReactNode } from 'react';
import { cn } from '../../utils/ui';

type ChipSize = 'sm' | 'md' | 'lg';
type ChipVariant = 'filled' | 'outline';

interface ChipProps {
    size?: ChipSize;
    variant?: ChipVariant;
    className?: string;
    onClick?: () => void;
    children?: ReactNode;
}

// Helper functions for styles
function getBaseStyles() {
    return 'flex items-center max-w-max rounded-md px-2 transition-all';
}

function getSizeStyles(size: ChipSize) {
    if (size === 'lg') return 'h-8 text-base font-medium';
    if (size === 'md') return 'h-7 text-sm font-medium';
    if (size === 'sm') return 'h-6 text-xs';
    return ''; // Default fallback
}

function getVariantStyles(variant: ChipVariant) {
    const filledStyles = 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-50';
    const outlineStyles = 'border border-gray-900 text-gray-900 dark:border-gray-50 dark:text-gray-50';

    if (variant === 'outline') return outlineStyles;
    return filledStyles; // Default to filled
}

function getClassName(size: ChipSize, variant: ChipVariant, className: string) {
    return cn(getBaseStyles(), getSizeStyles(size), getVariantStyles(variant), className);
}

const Chip: FC<ChipProps> = props => {
    const { children, size = 'md', variant = 'filled', className = '', onClick } = props;

    return (
        <div className={getClassName(size, variant, className)} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
            {children}
        </div>
    );
};

export default Chip;
